import { createForm } from '@seek/forms-ui';
import type { Private } from '@seek/indie-api-types';
import { isEmail, required } from '@seek/validators-js';
import {
  Actions,
  Alert,
  Button,
  Stack,
  Text,
  TextField,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';

import { RoleSelector } from '../RoleSelector.tsx';

import { useApi } from '#src/api/index.ts';
import { useOrganizations } from '#src/pages/manage/organizations/OrganizationContext.tsx';

export interface FormData {
  email: string;
  role: Private.Auth0UserInput['role'];
}

const { FormProvider, useField } = createForm<
  FormData,
  Record<string, never>,
  'en'
>();

interface FormProps {
  onSubmit: (event: any) => void;
  closeDialog: () => void;
  creating: boolean;
}

const Form = ({ onSubmit, closeDialog, creating }: FormProps) => {
  const email = useField({ id: 'email', validators: [required, isEmail] });
  const role = useField({ id: 'role', validators: [required] });

  return (
    <form onSubmit={onSubmit}>
      <Stack space="medium">
        <TextField
          type="email"
          id="email"
          label="Email"
          value={email.value}
          message={email.errorMessage}
          onChange={email.onChange}
          onBlur={email.onBlur}
          tone={email.valid ? 'neutral' : 'critical'}
        />
        <RoleSelector
          value={role.value}
          onChange={role.onChange}
          onBlur={role.onBlur}
        />
        <Actions>
          <Button type="submit" loading={creating}>
            {creating ? 'Creating' : 'Create'}
          </Button>
          <Button variant="soft" onClick={closeDialog}>
            Cancel
          </Button>
        </Actions>
      </Stack>
    </form>
  );
};

const initialValues: FormData = {
  email: '',
  role: 'read-only',
};

interface NewUserFormProps {
  closeDialog: () => void;
}

export const NewUserForm = ({ closeDialog }: NewUserFormProps) => {
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { refresh } = useOrganizations();

  const api = useApi();
  const showToast = useToast();

  const onSuccessfulCreate = () => {
    closeDialog();
    setErrorMessage('');
    showToast({
      tone: 'positive',
      message: "We've sent a welcome email if this user doesn't already exist",
    });
  };

  const createNewUser = async ({ email, role }: FormData) => {
    const userDetails = {
      emailAddress: email,
      role,
    };

    setCreating(true);

    try {
      await api.organizations.addUser(userDetails);
      onSuccessfulCreate();
      await refresh();
    } catch (err) {
      setErrorMessage(String(err));
    }

    setCreating(false);
  };

  return (
    <FormProvider initialValues={initialValues}>
      {({ handleSubmit }) => (
        <Stack space="medium">
          <Form
            creating={creating}
            closeDialog={closeDialog}
            onSubmit={handleSubmit((values) => {
              createNewUser(values);
            })}
          />
          {!errorMessage ? null : (
            <Alert tone="critical">
              <Stack space="small">
                <Text>There was a problem creating the user.</Text>
                <Text size="small">{errorMessage}</Text>
              </Stack>
            </Alert>
          )}
        </Stack>
      )}
    </FormProvider>
  );
};
